/**
 * misc.js
 */


import { parseISO } from "date-fns";
import { ARREST_DEMOGRAPHIC_START_DATE } from "./constants";


export const uuidv4 = () => {
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
    (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> +c / 4))).toString(16)
  );
}


export const sleep = (ms) => {
  return new Promise(resolve => setTimeout(resolve, ms));
}


export const objectToUrlParams = (obj) => {
  const urlSearchParams = new URLSearchParams();
  for (let [key, value] of Object.entries(obj)) {
    if (Array.isArray(value)) {
      value.forEach(v => urlSearchParams.append(key, v));
    } else {
      urlSearchParams.append(key, value);
    }
  }
  return urlSearchParams;
}


export const sortUsers = (user) => {
  return (a, b) => {
    // sort by user first (usually self), then name alphabetically
    if (user) {
      if (a.id === user.id) return -1;
      if (b.id === user.id) return 1;
    }
    if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
    if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
    return 0;
  }
}


// checks if string is a number 
// https://stackoverflow.com/questions/175739/how-can-i-check-if-a-string-is-a-valid-number
export const isNumeric = (str) => {
  if (typeof str != "string") return false // we only process strings!  
  return !isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
    !isNaN(parseFloat(str)) // ...and ensure strings of whitespace fail
}


// rounds a number to 2 decimal places.
// if number ends up rounded to zero then use the original input.
export const displayNumber = (number) => {
  const output = number.toFixed(2).replace(/(\.0+|0+)$/, '');
  return +output === 0 ? number : output;
}


// converts supervisor or trainer structures to multi-select
// options based on user's role.
export const getMultiSelectHandlerOptions = (user, supervisors, managedHandlers) => {
  if (user.role.isSupervisor && supervisors) {
    return supervisors
      .filter(s => s.handlers.length > 0)
      .map(s => ({
        id: s.id,
        label: `Supervised By ${s.id === user.id ? 'Me' : s.name} (${s.handlers.length})`,
        isHeader: true,
        options: s.handlers.map(h => ({
          id: h.id,
          label: h.id === user.id ? 'Me' : h.name,
          isHeader: false,
        }))
      }));
  } else if (user.role.isTrainer && managedHandlers) {
    return managedHandlers.map(h => ({
      id: h.id,
      label: `${h.id === user.id ? 'Me' : h.name}`,
      isHeader: false
    }));
  } else {
    return [];
  }
}


// In certain mobile-width contexts when the Intercom message window is open and the Launcher is hidden there isn't
// any way to close it.  This ensures that the Launcher is open and that a click will both hide the message
// window and hide the Launcher
export const forceShowIntercomLauncherIfIntercomOpen = () => {
  let intercomLauncherFrame = document.getElementsByClassName('intercom-launcher-frame')[0];
  if (intercomLauncherFrame) {
    intercomLauncherFrame.classList.add('intercom-launcher-force-show');
    intercomLauncherFrame.contentWindow.document
      .getElementById('intercom-container-body')
      .addEventListener(
        'click',
        () => intercomLauncherFrame.classList.remove('intercom-launcher-force-show'),
        { once: true }
      );
  }
}

export const showIntercomArticle = (articleId, isMobileContext = false) => {
  window.Intercom('showArticle', articleId);

  if (isMobileContext) {
    setTimeout(() => forceShowIntercomLauncherIfIntercomOpen(), 500);
  }
};


// checks if either of given dates are before the date when we started collecting arrest demographic data
export const isBeforeArrestDemographicStartDate = (date1, date2) => {
  if (typeof date1.getMonth !== 'function') {
    date1 = parseISO(date1);
  }
  if (date2 && typeof date2.getMonth !== 'function') {
    date2 = parseISO(date2);
  }

  return (
    date1 < ARREST_DEMOGRAPHIC_START_DATE 
    && (!date2 || date2 < ARREST_DEMOGRAPHIC_START_DATE)
  );
}


// In formState.dirtyFields, react-hook-form represents nested fields
// as objects/arrays, so we need to recursively search for true values
// to determine if a nested structure is dirty.
export const isFieldDirty = (value) => {
  if (!value) {  // undefined or false
    return false;
  } else if (value === true) {
    return true;
  } else {  // nested object
    const nestedFields = Array.isArray(value)
      ? value
      : Object.values(value);  // non-array object
    return nestedFields.some(v => isFieldDirty(v));
  }
}